/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useState } from "react";
import {
  CalendarX2,
  FastForward,
  HelpCircle,
  MessageCircleQuestion,
  OctagonMinus,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  UserMinus,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  advanceTimers,
  getDemoCapability,
  resetDemo,
  runFreedSlot,
  seedOffer,
  seedTransition,
  sendReply,
  type DemoCapability,
} from "../api/demo";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";

type ScenarioState = { running: string | null; lastOutcome: string | null };

type Scenario = {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  watch: string;
  /** Short, accurate toast shown on success (e.g. "Reset complete"). */
  toast: string;
  /** Optional inline controls a scenario needs (e.g. the multi-match second-family picker). */
  controls?: React.ReactNode;
  run: () => Promise<string>;
};

/**
 * Staging-only scenario triggers for live demos. Every button drives the real pipeline — real
 * staging-Healthie bookings, the real decision engine, real SMS to the demo phone — so what the
 * audience sees is exactly what production does.
 */
export function Demo() {
  useChrome({ title: "Demo" }, []);
  const { showToast } = useToast();
  const [capability, setCapability] = useState<DemoCapability | null>(null);
  const [checked, setChecked] = useState(false);
  const [toPhone, setToPhone] = useState<string>("");
  const [secondPhone, setSecondPhone] = useState<string>("");
  const [state, setState] = useState<ScenarioState>({ running: null, lastOutcome: null });

  useEffect(() => {
    void getDemoCapability().then((result) => {
      if (result.ok) {
        setCapability(result.value);
        const [first, second] = result.value.recipients;
        setToPhone(first?.phone ?? "");
        setSecondPhone(second?.phone ?? first?.phone ?? "");
      }
      setChecked(true);
    });
  }, []);

  const runScenario = useCallback(
    async (scenario: Scenario) => {
      setState({ running: scenario.id, lastOutcome: null });
      try {
        const outcome = await scenario.run();
        setState({ running: null, lastOutcome: outcome });
        showToast("success", scenario.toast);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Scenario failed";
        setState({ running: null, lastOutcome: message });
        showToast("error", message);
      }
    },
    [showToast],
  );

  if (!checked) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (capability === null) {
    return (
      <p className="max-w-xl text-sm text-text-secondary">
        Demo tools are not available in this environment. They exist on staging only and are never
        deployed to production.
      </p>
    );
  }

  const secondPicker = (
    <Select
      label="Second family texts"
      value={secondPhone}
      onChange={setSecondPhone}
      disabled={state.running !== null}
      options={capability.recipients
        .filter((r) => r.phone !== toPhone)
        .map((r) => ({ value: r.phone, label: r.label, description: r.phone }))}
    />
  );
  const scenarios = buildScenarios(toPhone, secondPhone, capability, secondPicker);
  const selected = capability.recipients.find((r) => r.phone === toPhone);

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-text-secondary">
        Each button drives the real pipeline on staging: real Healthie bookings, the real decision
        engine, and real texts to the family below. Run{" "}
        <span className="font-medium text-text-primary">Reset demo state</span> between scenarios.
      </p>
      <div className="max-w-sm space-y-1.5">
        <Select
          label="Texts go to"
          value={toPhone}
          onChange={setToPhone}
          disabled={state.running !== null}
          options={capability.recipients.map((r) => ({
            value: r.phone,
            label: r.label,
            description: r.phone,
          }))}
        />
        <p className="text-xs text-text-tertiary">
          Reset points the demo families at {selected?.label ?? "this number"} — reset after
          switching.
        </p>
      </div>
      {state.lastOutcome !== null && (
        <p className="rounded-2xl border border-border bg-bg-elevated px-4 py-3 text-sm text-text-primary">
          {state.lastOutcome}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {scenarios.map((scenario) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            running={state.running}
            onRun={() => void runScenario(scenario)}
          />
        ))}
      </div>
    </div>
  );
}

function ScenarioCard({
  scenario,
  running,
  onRun,
}: {
  scenario: Scenario;
  running: string | null;
  onRun: () => void;
}) {
  const Icon = scenario.icon;
  const busy = running === scenario.id;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-bg-elevated p-5">
      <div className="flex items-center gap-2.5">
        <Icon size={18} className="text-accent" />
        <h2 className="text-sm font-semibold text-text-primary">{scenario.title}</h2>
      </div>
      <p className="flex-1 text-sm leading-relaxed text-text-secondary">{scenario.description}</p>
      <p className="text-xs leading-relaxed text-text-tertiary">Watch: {scenario.watch}</p>
      {scenario.controls}
      <Button variant="primary" size="sm" disabled={running !== null} onClick={onRun}>
        {busy ? (
          <>
            <Spinner size="xs" className="text-bg-base" />
            Running…
          </>
        ) : (
          "Run"
        )}
      </Button>
    </div>
  );
}

function buildScenarios(
  toPhone: string,
  secondPhone: string,
  capability: DemoCapability,
  secondPicker: React.ReactNode,
): Scenario[] {
  const unwrap = async <T,>(promise: Promise<{ ok: boolean; value?: T; error?: { message: string } }>, outcome: string) => {
    const result = await promise;
    if (!result.ok) {
      throw new Error(result.error?.message ?? "request failed");
    }
    return outcome;
  };
  // Reply scenarios are self-contained: seed a fresh offer to the bookable family, then reply —
  // so a button never silently no-ops for want of a live offer.
  const seedThenReply = async (body: string, outcome: string) => {
    const seeded = await seedOffer(toPhone, {
      patientHealthieId: capability.bookablePatientHealthieId,
    });
    if (!seeded.ok) {
      throw new Error(seeded.error.message);
    }
    return unwrap(sendReply(toPhone, body), outcome);
  };
  return [
    {
      id: "freed-slot",
      icon: CalendarX2,
      title: "A slot frees up",
      description:
        "Books a real intake with the demo provider in staging Healthie, waits for it to sync, then cancels it. The cancellation is the trigger the agent watches for.",
      watch:
        "Agents → Runs fills in within a minute: the agent ranks the waitlist and texts the demo phone. Takes 30 to 60 seconds to start.",
      toast: "Slot freed — offer sending",
      run: () =>
        unwrap(
          runFreedSlot(toPhone),
          "Slot freed. The agent is deciding now; the offer text lands on the demo phone within a minute.",
        ),
    },
    {
      id: "reply-yes",
      icon: ThumbsUp,
      title: "Family replies YES",
      description:
        "Seeds a fresh offer to the family, then replies YES. The agent books the appointment in Healthie for real and texts a confirmation.",
      watch: "The confirmation text on the demo phone, the booked appointment in Healthie, and the run ending in Booked.",
      toast: "YES delivered",
      run: () => seedThenReply("YES", "YES sent. Booking and confirmation follow within seconds."),
    },
    {
      id: "reply-no",
      icon: ThumbsDown,
      title: "Family replies NO",
      description:
        "Seeds a fresh offer, then declines in natural language. The agent classifies it and moves on to the next family.",
      watch: "The run shows the decline and the agent reaching out to the next family.",
      toast: "NO delivered",
      run: () =>
        seedThenReply(
          "No thanks, we already found another provider",
          "Decline sent. The agent records it and moves down the list.",
        ),
    },
    {
      id: "reply-question",
      icon: MessageCircleQuestion,
      title: "Family asks a question",
      description:
        "Seeds a fresh offer, then replies with a question instead of yes or no. The agent will not guess: it hands the case to a coordinator.",
      watch: "Action Required in the web app inbox (Default Coordinator) and this console's Inbox, with a Healthie link and the family's words.",
      toast: "Question delivered",
      run: () =>
        seedThenReply(
          "maybe, is that covered by our insurance? what time exactly",
          "Question sent. The case reaches the coordinator inbox within a minute.",
        ),
    },
    {
      id: "reply-stop",
      icon: OctagonMinus,
      title: "Family texts STOP",
      description:
        "Seeds a fresh offer, then texts STOP. Compliance path: the offer is withdrawn, the number is opted out, and the agent never texts it again.",
      watch: "The offer flips to opted out; a follow-up scenario against this phone refuses to send until you reset.",
      toast: "STOP delivered",
      run: () => seedThenReply("STOP", "STOP sent. The offer is withdrawn and the phone is opted out."),
    },
    {
      id: "low-confidence",
      icon: HelpCircle,
      title: "Low-confidence case",
      description:
        "Stages an offer to a lower-ranked family with a thin profile, then replies YES. Confidence lands below the auto-book threshold, so a coordinator gets it instead.",
      watch: "Action Required in both inboxes with the confidence score and reasoning; no automatic booking.",
      toast: "Low-confidence case raised",
      run: async () => {
        const seeded = await seedOffer(toPhone, { rank: 2, score: "0.40" });
        if (!seeded.ok) {
          throw new Error(seeded.error.message);
        }
        return unwrap(
          sendReply(toPhone, "YES"),
          "Low-confidence YES sent. The case lands with a coordinator, not an auto-book.",
        );
      },
    },
    {
      id: "multi-match",
      icon: Users,
      title: "Two matches — no reply, next family",
      description:
        "A freed slot with two eligible families. The top family is texted first; the second family is put on the phone below so the next offer reaches a different number. Use \"Fast-forward the wait\" next to time the first family out.",
      watch: "The offer text to the first phone, then (after fast-forward) a second offer to the second phone as the agent moves on.",
      toast: "Slot freed — offer sending",
      controls: secondPicker,
      run: () =>
        unwrap(
          runFreedSlot(toPhone, secondPhone),
          "Two families match. The top-ranked one is being texted now; when they don't reply, use Fast-forward to advance to the second family.",
        ),
    },
    {
      id: "provider-transition",
      icon: UserMinus,
      title: "A provider leaves",
      description:
        "Casts a family as displaced by a departing provider, then frees a slot. Because the agent puts transitioning families ahead of the whole waitlist, this family is ranked first and texted before anyone else. Resets first, so run it on a clean slate.",
      watch:
        "In the run's waitlist ranking, the top family carries a \"provider transition\" tag; the offer text lands on the phone above.",
      toast: "Provider transition staged",
      run: async () => {
        const reset = await resetDemo(toPhone);
        if (!reset.ok) {
          throw new Error(reset.error.message);
        }
        const seeded = await seedTransition();
        if (!seeded.ok) {
          throw new Error(seeded.error.message);
        }
        return unwrap(
          runFreedSlot(toPhone),
          "A transitioning family now tops the waitlist. The agent is deciding; the offer text lands within a minute.",
        );
      },
    },
    {
      id: "advance-timers",
      icon: FastForward,
      title: "Fast-forward the 1-hour wait",
      description:
        "Skips the real 60-minute no-response window: ages the active offer so the agent times out the current family and texts the next one within ~30 seconds.",
      watch: "Within ~30s the current offer flips to TIMED OUT and the next family gets a text.",
      toast: "Timers advanced",
      run: () =>
        unwrap(
          advanceTimers(),
          "Timers advanced. The current family times out and the agent texts the next one within ~30 seconds.",
        ),
    },
    {
      id: "reset",
      icon: RotateCcw,
      title: "Reset demo state",
      description:
        "Closes every in-flight offer and conversation, cancels prior test bookings, clears the demo phone's rate limit and opt-out, reopens the fixture waitlist, and expires open coordinator handoffs. Run between scenarios.",
      watch: "A clean slate: the next scenario starts from nothing.",
      toast: "Reset complete",
      run: () => unwrap(resetDemo(toPhone), "Demo state reset. Ready for the next scenario."),
    },
  ];
}

